import { useStore } from "@livestore/react";
import { dev } from "@phosphor/devstring";
import { useMemo, useRef } from "react";
import { defineLiveStoreDemo } from "#shared/demos/defineLiveStoreDemo.tsx";
import { useStateMachine } from "#sources/autocomplete-demo/hooks/useStateMachine.ts";
import { TodoVMContext, useTodoVM } from "#sources/view-model-interfaces/todo-vm/TodoVMContext.tsx";
import { Footer } from "#sources/view-model-interfaces/todo-vm/components/Footer.tsx";
import { Header } from "#sources/view-model-interfaces/todo-vm/components/Header.tsx";
import { MainSection } from "#sources/view-model-interfaces/todo-vm/components/MainSection.tsx";
import { events, schema } from "#sources/view-model-interfaces/todo-vm/livestore/schema.ts";
import { createTodoListScope } from "#sources/view-model-interfaces/todo-vm/scope.ts";
import todoCss from "./todo-vm.css" with { type: "text" };

export default defineLiveStoreDemo({
  source: dev`todo-vm`,
  schema,
  css: String(todoCss),
  boot: async (store) => {
    store.commit(
      events.uiStateSet({ filter: "all", newTodoText: "" }),
      events.todoCreated({ id: "todo_1", text: "🧺 Sort the whites" }),
      events.todoCreated({ id: "todo_2", text: "🌀 Start the washing machine" }),
    );
  },
  RootComponent({ store }) {
    const vm = useMemo(() => createTodoListScope(store), [store]);
    const sectionRef = useRef<HTMLElement>(null);
    const __interrupt__ = useStateMachine(
      "🧴 Buy fabric softener\n🧹 Sweep the floors\n!#...",
      {
        "\n": () => {
          vm.header.addTodo();
          return 400;
        },
        "!": () => (store.query(vm.itemList.items$)[1]?.toggleCompleted(), 1500),
        "#": () => (store.query(vm.itemList.items$)[2]?.toggleCompleted(), 1500),
        ".": () => 1000,
        orDefault: (letter) => {
          const currentText = store.query(vm.header.newTodoText$);
          vm.header.updateNewTodoText(currentText + letter);
          return Math.random() * 150 + 50;
        },
        reset: () => (vm.reset(), 2000),
      },
      { observeRef: sectionRef },
    );
    return (
      <TodoVMContext.Provider value={vm}>
        <section className="todos" ref={sectionRef} onClickCapture={__interrupt__} onFocusCapture={__interrupt__}>
          <Header />
          <MainSection />
          <Footer />
        </section>
      </TodoVMContext.Provider>
    );
  },
});

function StateMachine({ children }: { children: React.ReactNode }) {
  const vm = useTodoVM();
  const { store } = useStore();
}
